import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Shared icon actions for admin table rows.
 * Every page uses the same size, shape, hover and danger treatment;
 * `action` picks a preset icon + label, `icon` / `label` override it.
 */
const PRESETS = {
    view: { icon: 'ri-eye-line', label: 'View' },
    edit: { icon: 'ri-pencil-line', label: 'Edit' },
    delete: { icon: 'ri-delete-bin-6-line', label: 'Delete', tone: 'danger' },
    copy: { icon: 'ri-file-copy-line', label: 'Copy' },
    reply: { icon: 'ri-reply-line', label: 'Reply' },
    show: { icon: 'ri-eye-line', label: 'Show' },
    hide: { icon: 'ri-eye-off-line', label: 'Hide' },
    more: { icon: 'ri-more-2-fill', label: 'More actions' },
};

export const AdminActionButton = React.forwardRef(function AdminActionButton({
    action,
    icon,
    label,
    tone,
    to,
    href,
    onClick,
    disabled = false,
    className = '',
    children,
    ...rest
}, ref) {
    const preset = PRESETS[action] || {};
    const name = label || preset.label || '';
    const classes = [
        'admin-action',
        `admin-action--${tone || preset.tone || 'neutral'}`,
        children ? 'admin-action--text' : '',
        className,
    ].filter(Boolean).join(' ');
    const content = (
        <>
            <i className={icon || preset.icon} aria-hidden="true" />
            {children}
        </>
    );

    if (to) {
        return (
            <Link ref={ref} to={to} className={classes} title={name} aria-label={children ? undefined : name} {...rest}>
                {content}
            </Link>
        );
    }

    if (href) {
        return (
            <a ref={ref} href={href} className={classes} title={name} aria-label={children ? undefined : name} {...rest}>
                {content}
            </a>
        );
    }

    return (
        <button
            ref={ref}
            type="button"
            className={classes}
            title={name}
            aria-label={children ? undefined : name}
            onClick={onClick}
            disabled={disabled}
            {...rest}
        >
            {content}
        </button>
    );
});

export default function AdminRowActions({ children, className = '' }) {
    return <div className={`admin-row-actions ${className}`.trim()}>{children}</div>;
}
